// src/pages/buyer/BuyerDashboard.jsx
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { buyerDashboardApi } from '@/api/services'
import { useAuth } from '@/context/AuthContext'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { Card, CardHeader, CardBody, StatusBadge, PageSpinner } from '@/components/common/ui'
import {
  Package, ShoppingCart, Star, Bell, Heart, ChevronRight,
  TrendingUp, FileText, Truck, Clock, CheckCircle, AlertTriangle,
  ArrowUpRight, ArrowDownRight,
} from 'lucide-react'
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts'

export default function BuyerDashboard() {
  const { user } = useAuth()
  const { format } = useCurrency()

  const { data: res, isLoading } = useQuery({
    queryKey: ['buyer-dashboard'],
    queryFn: () => buyerDashboardApi.index(),
    refetchInterval: 120_000,
  })

  if (isLoading) return <PageSpinner />

  const d = res?.data?.data || res?.data || {}
  const orderStats   = d.order_stats || {}
  const spend        = d.spend || {}
  const spendTrend   = d.spend_trend || []
  const recentOrders = d.recent_orders || []
  const activeSamples = d.active_samples || []
  const sampleCounts = d.sample_counts || {}
  const activeRfqs   = d.active_rfqs || []
  const rfqCounts    = d.rfq_counts || {}
  const profilePct   = d.profile_complete?.percent || 0
  const unread       = d.unread_notifications || 0

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-brand rounded-3xl p-7 text-white relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="font-display font-black text-2xl mb-1">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-white/80 text-sm">Here's your business overview at a glance.</p>
        </div>
        {profilePct < 100 && (
          <Link to="/buyer/profile" className="relative z-10 mt-4 inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 text-sm font-bold hover:bg-white/30 transition-colors">
            Profile {profilePct}% complete — finish setup
          </Link>
        )}
      </div>

      {/* KPI Cards Row 1 — Orders & Spend */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Spend"
          value={format(spend.total || 0)}
          sub={`This month: ${format(spend.this_month || 0)}`}
          icon={TrendingUp}
          color="pink"
        />
        <KpiCard
          label="Total Orders"
          value={orderStats.total || 0}
          sub={`${orderStats.active || 0} active`}
          icon={Package}
          color="blue"
        />
        <KpiCard
          label="Avg Order Value"
          value={spend.avg_order_value ? format(spend.avg_order_value) : '—'}
          sub="Per order"
          icon={ShoppingCart}
          color="green"
        />
        <KpiCard
          label="Notifications"
          value={unread}
          sub="Unread"
          icon={Bell}
          color="amber"
          to="/buyer/notifications"
        />
      </div>

      {/* KPI Cards Row 2 — Samples & RFQs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Sample Requests"
          value={sampleCounts.total || 0}
          sub={`${sampleCounts.pending || 0} pending`}
          icon={Truck}
          color="indigo"
          to="/buyer/samples"
        />
        <KpiCard
          label="Dispatched Samples"
          value={sampleCounts.dispatched || 0}
          sub={`${sampleCounts.delivered || 0} delivered`}
          icon={CheckCircle}
          color="teal"
          to="/buyer/samples"
        />
        <KpiCard
          label="Open RFQs"
          value={rfqCounts.open || 0}
          sub={`${rfqCounts.awarded || 0} awarded`}
          icon={FileText}
          color="purple"
          to="/buyer/rfqs"
        />
        <KpiCard
          label="Active Orders"
          value={orderStats.active || 0}
          sub={`${orderStats.delivered || 0} delivered`}
          icon={Clock}
          color="orange"
          to="/buyer/orders"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { to: '/fabrics',         label: 'Browse Fabrics',    emoji: '🧵' },
          { to: '/buyer/orders',    label: 'My Orders',         emoji: '📦' },
          { to: '/buyer/samples',   label: 'Samples',           emoji: '🎨' },
          { to: '/buyer/rfqs',      label: 'My RFQs',           emoji: '📋' },
          { to: '/buyer/wishlists', label: 'Wishlists',          emoji: '❤️' },
        ].map(a => (
          <Link key={a.to} to={a.to}
            className="flex flex-col items-center gap-2 p-4 bg-white border border-border rounded-2xl hover:border-pink hover:shadow-card-md transition-all text-center">
            <span className="text-2xl">{a.emoji}</span>
            <span className="text-xs font-bold">{a.label}</span>
          </Link>
        ))}
      </div>

      {/* Spend Trend Chart + Recent Orders */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Spend Trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="font-bold text-sm">Monthly Spend</h2>
          </CardHeader>
          <CardBody>
            {spendTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={spendTrend}>
                  <defs>
                    <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Spend']} labelStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="spend" stroke="#ec4899" fill="url(#spendGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-10 text-muted text-sm">No spend data yet</div>
            )}
          </CardBody>
        </Card>

        {/* Recent Orders */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <h2 className="font-bold text-sm">Recent Orders</h2>
            <Link to="/buyer/orders" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              View all <ChevronRight size={12} />
            </Link>
          </CardHeader>
          {recentOrders.length === 0 ? (
            <CardBody>
              <div className="text-center py-10">
                <div className="text-3xl mb-2">📦</div>
                <p className="text-sm text-muted">No orders yet</p>
                <Link to="/fabrics" className="btn-primary mt-3 text-xs px-5 py-2 inline-flex">Shop Now</Link>
              </div>
            </CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {recentOrders.map(order => (
                <Link key={order.id} to={`/buyer/orders/${order.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-pink/10 flex items-center justify-center flex-shrink-0">
                    <Package size={16} className="text-pink" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">#{order.order_number}</p>
                    <p className="text-[11px] text-muted">
                      {order.supplier?.business_name || order.supplier?.name} · {new Date(order.created_at).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold">{format(order.total_amount)}</p>
                    <StatusBadge status={order.status} />
                  </div>
                </Link>
              ))}
            </CardBody>
          )}
        </Card>
      </div>

      {/* Active Samples & RFQs */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Samples */}
        <Card>
          <CardHeader>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <Truck size={14} className="text-indigo-500" /> Active Samples
            </h2>
            <Link to="/buyer/samples" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              View all <ChevronRight size={12} />
            </Link>
          </CardHeader>
          {activeSamples.length === 0 ? (
            <CardBody>
              <div className="text-center py-8 text-muted text-sm">
                <p>No active samples</p>
                <Link to="/fabrics" className="text-pink font-bold text-xs hover:underline mt-1 inline-block">Request a sample</Link>
              </div>
            </CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {activeSamples.map(sr => (
                <Link key={sr.id} to="/buyer/samples"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-surface border border-border overflow-hidden flex-shrink-0">
                    {sr.product?.primary_image_url && (
                      <img src={sr.product.primary_image_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 line-clamp-1">{sr.product?.name}</p>
                    <p className="text-[11px] text-muted">{sr.supplier?.business_name || sr.supplier?.name}</p>
                  </div>
                  <StatusBadge status={sr.status} />
                </Link>
              ))}
            </CardBody>
          )}
        </Card>

        {/* RFQs */}
        <Card>
          <CardHeader>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <FileText size={14} className="text-purple-500" /> Active RFQs
            </h2>
            <Link to="/buyer/rfqs" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              View all <ChevronRight size={12} />
            </Link>
          </CardHeader>
          {activeRfqs.length === 0 ? (
            <CardBody>
              <div className="text-center py-8 text-muted text-sm">
                <p>No active RFQs</p>
                <Link to="/buyer/rfqs" className="text-pink font-bold text-xs hover:underline mt-1 inline-block">Create an RFQ</Link>
              </div>
            </CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {activeRfqs.map(rfq => (
                <Link key={rfq.id} to={`/buyer/rfqs/${rfq.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <FileText size={16} className="text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 line-clamp-1">{rfq.title}</p>
                    <p className="text-[11px] text-muted">
                      {rfq.quantity_metres}m · {rfq.quotes_count || 0} quote{(rfq.quotes_count || 0) !== 1 ? 's' : ''}
                      {rfq.deadline && ` · Due ${new Date(rfq.deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                    </p>
                  </div>
                  <StatusBadge status={rfq.status} />
                </Link>
              ))}
            </CardBody>
          )}
        </Card>
      </div>
    </div>
  )
}

// ── Reusable KPI Card ──────────────────────────────────────────────────────────
const COLORS = {
  pink:   'bg-pink/10 text-pink',
  blue:   'bg-blue-100 text-blue-600',
  green:  'bg-green-100 text-green-600',
  amber:  'bg-amber-100 text-amber-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  teal:   'bg-teal-100 text-teal-600',
  purple: 'bg-purple-100 text-purple-600',
  orange: 'bg-orange-100 text-orange-600',
}

function KpiCard({ label, value, sub, icon: Icon, color = 'pink', to }) {
  const Wrapper = to ? Link : 'div'
  const wrapperProps = to ? { to } : {}
  return (
    <Wrapper {...wrapperProps}
      className={`bg-white border border-border rounded-xl p-4 ${to ? 'hover:border-pink hover:shadow-card-md transition-all cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${COLORS[color]}`}>
          <Icon size={18} />
        </div>
      </div>
      <p className="text-2xl font-extrabold text-gray-900">{value}</p>
      <p className="text-[11px] text-muted font-bold uppercase tracking-wider mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-muted mt-0.5">{sub}</p>}
    </Wrapper>
  )
}
